/**
 * The handler map — keyed by tool name, with **no default branch**.
 *
 * This is defence in depth, NOT the gate. The gate is
 * `src-tauri/src/mcp/capability.rs::authorize`, which runs before the emit and
 * denies any name that has no `ToolDef`; a handler here can never be reached
 * for a tool the user did not grant. What this map adds is that a name Rust
 * somehow let through still lands nowhere: an unknown key returns an error
 * object rather than throwing, and calls no store function.
 *
 * **The strongest single constraint in the feature lives here**: every
 * `invoke()` reachable from this module graph must be on the allow-list in
 * `mcpAttackSurface.test.js`, which DERIVES the file set by walking the real
 * import graph from `bus.js` and this file. It goes red the moment anyone adds
 * a tool that reaches a command that is not on it — including the bridge
 * installer, which is a `#[tauri::command]` for the settings page and must
 * never be reachable from a connected MCP client.
 *
 * The allow-list is not "four extension-record commands", which is what this
 * comment used to claim and what the directory-walking version of that test
 * appeared to prove. The graph leaves `src/mcp/` into `documentsStore`,
 * `boardsStore`, `documentsMemory` and `itemLocation`, so the honest list is
 * ten; the test names each one and why. (It was sixteen until 17.09.2026, when
 * `personalSync` and `utils/tauri` left the graph: the scope getter and the
 * `isTauri` predicate this surface needs moved to the invoke-free
 * `services/personalScope` and `utils/isTauri`.) Of those,
 * `extension_records_sync_scope` is the dangerous one — it tombstones a whole
 * scope, phase 1 never calls it, and a second assertion there keeps `src/mcp/`
 * from starting to.
 */
import { listAllScopes, refuseWorkspaceScopeFor } from '../docScopes';
import { mcpError } from '../errors';
import { refreshUiAfterTool } from '../uiRefresh';
import { takeBoardRecords } from '../boardChanges';
import { scrubMediaKeysDeep } from '../mediaKeys';
import {
  documentsTree,
  documentRead,
  documentSubpages,
  documentCreate,
  documentCreateFolder,
  documentRename,
  documentWrite,
} from './documents';
import { memoryList, memoryRead, memoryAppend, memoryPurge } from './memory';
import { boardsList, boardRead } from './boards';
import {
  boardCreate,
  boardUpdate,
  boardListCreate,
  boardListRename,
  boardTaskCreate,
  boardTaskUpdate,
  boardTaskMove,
  boardCommentAdd,
} from './boardWrites';
import { boardDelete, boardListDelete, boardTaskDelete } from './boardDeletes';
import { documentDelete, documentMove } from './documentTree';
import { appointmentsList, appointmentRead } from './calendar';
import { appointmentCreate, appointmentUpdate } from './calendarWrites';
import { appointmentDelete } from './calendarDeletes';
import { collectionsList } from './collections';
import { boardTaskLinkCommit, boardTaskUnlinkCommit } from './boardCommitLinks';
import { graphNeighbors, graphPath, graphProject, graphSearch, resetGraphCache } from './graph';
import { collectionRead, requestRead } from './collectionReads';
import { environmentsList } from './environments';
import { requestDiagnose } from './requestDiagnose';
import { collectionCreate, folderCreate, requestCreate } from './collectionWrites';
import { specImport } from './collectionImport';
import {
  collectionUpdate,
  folderRename,
  requestMove,
  requestUpdate,
} from './collectionUpdates';
import { mockExampleCreate, mockExampleDelete } from './mockExamples';
import { collectionDelete, folderDelete, requestDelete } from './collectionDeletes';

/**
 * `ctx` carries the few things a handler needs from React-land — currently
 * only the translated Agent Notes folder name, so the folder an agent creates
 * is named in the user's own language.
 */
export const HANDLERS = Object.freeze({
  // Personal, the user's document workspaces (`personal/<id>`, card 4.4), teams.
  scopes_list: async () => ({ scopes: await listAllScopes() }),

  documents_tree: (args) => documentsTree(args),
  document_read: (args) => documentRead(args),
  document_subpages: (args) => documentSubpages(args),
  document_create_folder: (args) => documentCreateFolder(args),
  // `ctx.caller` names the MCP client the content is marked with (AI Act
  // Art. 50(2), services/ai/aiAuthorship.js).
  document_create: (args, ctx) => documentCreate(args, ctx),
  document_rename: (args) => documentRename(args),
  document_write: (args, ctx) => documentWrite(args, ctx),
  document_move: (args) => documentMove(args),
  document_delete: (args) => documentDelete(args),

  memory_list: (args, ctx) => memoryList(args, ctx),
  memory_read: (args) => memoryRead(args),
  memory_append: (args, ctx) => memoryAppend(args, ctx),
  memory_purge: (args) => memoryPurge(args),

  boards_list: (args) => boardsList(args),
  board_read: (args) => boardRead(args),

  board_create: (args, ctx) => boardCreate(args, ctx),
  board_update: (args) => boardUpdate(args),
  board_list_create: (args) => boardListCreate(args),
  board_list_rename: (args) => boardListRename(args),
  board_task_create: (args, ctx) => boardTaskCreate(args, ctx),
  board_task_update: (args, ctx) => boardTaskUpdate(args, ctx),
  board_task_move: (args) => boardTaskMove(args),
  board_comment_add: (args, ctx) => boardCommentAdd(args, ctx),
  // Commit ↔ task links (slice 5): hand-made links on a task.
  board_task_link_commit: (args, ctx) => boardTaskLinkCommit(args, ctx),
  board_task_unlink_commit: (args) => boardTaskUnlinkCommit(args),

  board_task_delete: (args) => boardTaskDelete(args),
  board_list_delete: (args) => boardListDelete(args),
  board_delete: (args) => boardDelete(args),

  appointments_list: (args) => appointmentsList(args),
  appointment_read: (args) => appointmentRead(args),

  appointment_create: (args) => appointmentCreate(args),
  appointment_update: (args) => appointmentUpdate(args),
  appointment_delete: (args) => appointmentDelete(args),

  collections_list: (args) => collectionsList(args),
  // Link graph (read only) — see handlers/graph.js for what never leaves.
  graph_search: (args) => graphSearch(args),
  graph_neighbors: (args) => graphNeighbors(args),
  graph_path: (args) => graphPath(args),
  graph_project: (args) => graphProject(args),
  collection_read: (args) => collectionRead(args),
  request_read: (args) => requestRead(args),
  environments_list: (args) => environmentsList(args),
  request_diagnose: (args) => requestDiagnose(args),

  collection_create: (args) => collectionCreate(args),
  folder_create: (args) => folderCreate(args),
  request_create: (args) => requestCreate(args),
  spec_import: (args) => specImport(args),

  request_update: (args) => requestUpdate(args),
  request_move: (args) => requestMove(args),
  collection_update: (args) => collectionUpdate(args),
  folder_rename: (args) => folderRename(args),

  mock_example_create: (args) => mockExampleCreate(args),
  mock_example_delete: (args) => mockExampleDelete(args),

  request_delete: (args) => requestDelete(args),
  folder_delete: (args) => folderDelete(args),
  collection_delete: (args) => collectionDelete(args),
});

/** Every tool name this side can serve — asserted against Rust's `TOOLS`. */
export const HANDLER_NAMES = Object.freeze(Object.keys(HANDLERS));

/**
 * Runs one tool. Returns `{ ok, result }` or `{ ok: false, error }` — it never
 * throws, and it never lets an exception's text out: a store error becomes a
 * fixed code and a fixed message.
 */
export async function runHandler(tool, args, ctx) {
  const handler = Object.prototype.hasOwnProperty.call(HANDLERS, tool) ? HANDLERS[tool] : null;
  if (!handler) {
    return { ok: false, error: mcpError('unknown_tool', 'This tool is not available.') };
  }
  // A document workspace (`personal/<id>`) passes Rust's shape check for every
  // tool; only the document tools take it (docScopes.js WORKSPACE_TOOLS).
  const wrongScope = refuseWorkspaceScopeFor(tool, args?.scope);
  if (wrongScope) return { ok: false, error: wrongScope };
  try {
    const result = await handler(args || {}, ctx || {});
    // Anything that is not a graph read may have changed what the graph
    // shows; the next graph call must see it (the cache is only seconds long,
    // but an agent creates then immediately looks).
    if (!tool.startsWith('graph_')) resetGraphCache();
    if (result && typeof result === 'object' && result.error) {
      // A refused write (conflict, too_large, not_found, unavailable,
      // cascade_required) must NOT refresh the UI: nothing changed, and a nonce
      // bump would make every open view re-read for nothing while suggesting it
      // did.
      //
      // A PARTIAL delete is the one exception, and it is not a loophole: a
      // handler stages records only for rows it actually changed, so a refusal
      // that touched nothing stages nothing and still cannot bump. Skipping the
      // refresh here would be worse than cosmetic — the open board tab would
      // keep cards that are already tombstoned in its in-memory snapshot, and
      // its next whole-board persist would RESURRECT them.
      if (takeBoardRecords(result)) await refreshUiAfterTool(tool, result);
      return { ok: false, error: scrubMediaKeysDeep(result) };
    }
    // The write landed — tell the open UI, so an external agent's folder or
    // document appears without the user navigating away and back, and (for a
    // create) so it appears somewhere VISIBLE rather than behind a collapsed
    // parent. Expand in place only; never navigate. See `mcp/uiRefresh.js` and
    // `mcp/reveal.js` for why this diverges from the in-app AI path on purpose.
    //
    // Awaited so the reveal request is parked before the nonce bump inside it
    // starts the sidebar's re-read; `refreshUiAfterTool` swallows its own
    // errors, so this cannot turn a landed write into `write_failed`.
    await refreshUiAfterTool(tool, result);
    // THE gate for image decryption keys (card MCP 1): every JS tool result
    // passes here, so a read path that forgets `renderBody` still cannot hand
    // an agent a `data-enc-key` or a block node's `k`. A copy — `result` may
    // share objects with the live store. See `mcp/mediaKeys.js`.
    return { ok: true, result: scrubMediaKeysDeep(result) };
  } catch (err) {
    // Deliberately not `err.message`: see src/mcp/errors.js.
    if (process.env.NODE_ENV !== 'test') console.warn('[mcp] handler failed:', tool, err);
    return { ok: false, error: mcpError('write_failed', 'The tool could not complete.') };
  }
}
